import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { finishSocialPulseLogin } from "@/lib/socialpulseOidc";
import { CheckCircle, WarningCircle } from "@phosphor-icons/react";

// `/auth/socialpulse-callback` — receives ?code&state from SocialPulse, hands them
// to our backend to mint the sp_session cookie, then refreshes AuthContext and
// bounces back to whatever page kicked off the flow.

export default function SocialPulseCallback() {
  const navigate = useNavigate();
  const { refresh, celebrateLogin } = useAuth();
  const [status, setStatus] = useState({ state: "loading", msg: "" });

  useEffect(() => {
    (async () => {
      try {
        const { returnTo } = await finishSocialPulseLogin(http);
        await refresh();
        setStatus({ state: "ok", msg: "" });
        celebrateLogin();
        setTimeout(() => navigate(returnTo || "/", { replace: true }), 400);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const status = e?.response?.status;
        const hasResponse = !!e?.response;
        // Only swap to the canned copy for genuine server/network errors
        // (5xx or no response at all). Client-side guard failures thrown
        // by finishSocialPulseLogin() (`state mismatch`, `missing code`,
        // IdP error_description) come through as bare Errors and must
        // surface their real message so the user knows what went wrong.
        let msg;
        if (hasResponse && typeof detail === "string" && detail.length < 200 && status < 500) {
          msg = detail;
        } else if (hasResponse && status >= 500) {
          msg = "Sign-in couldn't complete. Please try again or contact support if it keeps happening.";
        } else {
          msg = e?.message || "Sign-in failed";
        }
        setStatus({ state: "error", msg });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="min-h-screen flex items-center justify-center bg-[color:var(--np-bg,#F5EBDC)] text-[color:var(--np-text,#151312)] px-6">
      <div
        data-testid="socialpulse-callback"
        className="max-w-lg w-full border border-black/15 p-8 md:p-10 text-center bg-white/70 backdrop-blur"
      >
        <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-4">
          / SocialPulse SSO
        </div>

        {status.state === "loading" && (
          <>
            <div className="mx-auto mb-5 h-10 w-10 rounded-full border-2 border-black/10 border-t-[color:var(--np-brand,#B85F2A)] animate-spin" />
            <h1 className="font-serif-display text-3xl leading-tight mb-2">Signing you in…</h1>
            <p className="text-sm text-[color:var(--np-text-2,#3A3830)]">Verifying your identity with SocialPulse.</p>
          </>
        )}

        {status.state === "ok" && (
          <>
            <CheckCircle size={44} weight="thin" className="mx-auto text-emerald-500 mb-3" />
            <h1 className="font-serif-display text-3xl leading-tight mb-2">Welcome back.</h1>
            <p className="text-sm text-[color:var(--np-text-2,#3A3830)]">Redirecting…</p>
          </>
        )}

        {status.state === "error" && (
          <>
            <WarningCircle size={44} weight="thin" className="mx-auto text-red-500 mb-3" />
            <h1 className="font-serif-display text-3xl leading-tight mb-2">Couldn&apos;t finish sign-in</h1>
            <p className="text-sm text-[color:var(--np-text-2,#3A3830)] break-words">{status.msg}</p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Link to="/" className="font-mono-tech text-[10px] tracking-[0.3em] uppercase underline">Home</Link>
              <button
                data-testid="socialpulse-retry"
                onClick={() => window.location.replace("/")}
                className="font-mono-tech text-[10px] tracking-[0.3em] uppercase px-4 py-2 border border-black/25 hover:bg-[color:var(--np-text,#151312)] hover:text-white transition-colors"
              >
                Try again
              </button>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
